import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf, faSun, faMoon, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import emailjs from '@emailjs/browser';

const CustomSunIcon = ({ className, style }) => (
  <svg 
    className={className}
    style={style}
    width="1.6rem" 
    height="1.6rem" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    {/* Central sun circle */}
    <circle cx="12" cy="12" r="5" fill="currentColor"/>
    
    {/* Sun rays */}
    <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      {/* Vertical and horizontal rays */}
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </g>
  </svg>
);

const AboutUs = () => {
  const [isVisible, setIsVisible] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : true;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState({
    submitted: false,
    submitting: false,
    info: { error: false, msg: null }
  });

  // Clear status message after 2 seconds
  useEffect(() => {
    if (status.info.msg) {
      const timer = setTimeout(() => {
        setStatus(prev => ({
          ...prev,
          info: { error: false, msg: null }
        }));
      }, 2000);
  
      return () => clearTimeout(timer);
    }
  }, [status.info.msg]);

  // Apply theme
  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, [isDarkMode]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle scroll effects and cursor
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Only track cursor on non-touch devices
      if (windowWidth > 768 && cursorRef.current && cursorDotRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
        cursorDotRef.current.style.left = `${e.clientX}px`;
        cursorDotRef.current.style.top = `${e.clientY}px`;
      }
    };

    const handleScroll = () => {
      const elements = document.querySelectorAll('.fade-in');
      elements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        setIsVisible(prev => ({
          ...prev,
          [index]: rect.top <= window.innerHeight - 100
        }));
      });
    };

    // Create fewer particles for better performance
    const createParticles = () => {
      const particleCount = windowWidth > 768 ? 25 : 10; // Reduced count
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.width = `${Math.random() * (windowWidth > 768 ? 4 : 2)}px`; // Smaller particles
        particle.style.height = particle.style.width;
        particle.style.left = `${Math.random() * 100}vw`;
        particle.style.top = `${Math.random() * 100}vh`;
        document.body.appendChild(particle);
      }
    };

    createParticles();
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousemove', handleMouseMove);
      const existingParticles = document.querySelectorAll('.particle');
      existingParticles.forEach(particle => particle.remove());
    };
  }, [windowWidth]);

  const toggleTheme = () => {
    setIsDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(prevStatus => ({ ...prevStatus, submitting: true }));

    try {
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        message: formData.message,
        to_name: 'The Sustainable Formula Team',
      };

      await emailjs.send(
        'service_dj1oaxd', 
        'template_k4alktx', 
        templateParams,
        '2UhB7ce5WSmWrg-Xx' 
      );

      setStatus({
        submitted: true,
        submitting: false,
        info: { error: false, msg: 'Message sent successfully!' }
      });

      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setStatus({
        submitted: false,
        submitting: false,
        info: { error: true, msg: 'An error occurred. Please try again later.' }
      });
    }
  };

  const themeClass = isDarkMode ? 'dark-theme' : 'light-theme';

  // Simplified and optimized styles
  const styles = {
    desktopNav: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: windowWidth <= 768 ? '10px' : '20px',
      position: 'relative',
      zIndex: 100,
      transition: 'all 0.3s ease'
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      fontSize: windowWidth <= 480 ? '0.9rem' : 'clamp(1rem, 3vw, 1.5rem)',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: windowWidth <= 480 ? '205px' : 'none',
    },
    logoIcon: { marginRight: '8px' },
    navControls: {
      display: 'flex',
      alignItems: 'center',
      gap: windowWidth <= 480 ? '5px' : '10px',
    },
    desktopNavLinks: {
      display: windowWidth <= 768 ? 'none' : 'flex',
      gap: '20px',
    },
    navLink: {
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      textDecoration: 'none',
    },
    themeToggle: {
      background: 'none',
      border: 'none',
      fontSize: '1.2rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
      marginRight: '5px',
      transition: 'transform 0.3s ease'
    },
    hamburger: {
      display: windowWidth <= 768 ? 'block' : 'none',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? 'var(--dark-primary-green)' : '#15523E',
      cursor: 'pointer',
      padding: '5px',
    },
    mobileMenu: {
      display: mobileMenuOpen ? 'flex' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      zIndex: 1000,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    mobileMenuClose: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      color: isDarkMode ? '#2ecc71' : '#15523E',
      cursor: 'pointer',
    },
    mobileNavLinks: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      fontSize: '1.5rem',
    },
    mobileNavLink: {
      color: isDarkMode ? '#2ecc71' : '#15523E',
      textDecoration: 'none',
      padding: '10px',
    }
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

          :root {
            --primary-green: #2ecc71;
            --dark-green: #27ae60;
            --light-green: #52c234;
            --accent-green: #32de84;
            --dark-primary-green: #2ecc71;
          }

          body {
            font-family: 'Poppins', sans-serif;
            margin: 0;
            padding: 0;
            overflow-x: hidden;
          }

          body.light-theme {
            background-color: #fff;
            color: #000;
          }

          body.dark-theme {
            background-color: #000;
            color: #fff;
          }

          .particle {
            position: absolute;
            background: var(--primary-green);
            border-radius: 50%;
            opacity: 0.3;
            animation: float 20s infinite linear;
          }

          @keyframes float {
            0% { transform: translateY(0) rotate(0deg); }
            100% { transform: translateY(-100vh) rotate(360deg); }
          }

          .custom-cursor {
            width: 30px;
            height: 30px;
            border: 2px solid #2ecc71;
            border-radius: 50%;
            position: fixed;
            pointer-events: none;
            z-index: 9999;
            transform: translate(-50%, -50%);
            transition: width 0.3s ease, height 0.3s ease;
          }
          
          .cursor-dot {
            width: 4px;
            height: 4px;
            background-color: #2ecc71;
            border-radius: 50%;
            position: fixed;
            pointer-events: none;
            z-index: 9999;
            transform: translate(-50%, -50%);
          }

          .dark-theme .custom-cursor { border-color: #2ecc71; }
          .dark-theme .cursor-dot { background-color: #2ecc71; }
          .light-theme .custom-cursor { border-color: #15523E; }
          .light-theme .cursor-dot { background-color: #15523E; }

          a:hover ~ .custom-cursor,
          button:hover ~ .custom-cursor {
            width: 30px;
            height: 30px;
          }

          .about-hero {
            height: 50vh;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            position: relative;
            overflow: hidden;
            padding: 0 20px;
          }

          .hero-content {
            z-index: 1;
            max-width: 800px;
          }

          .hero-content h1 {
            font-size: clamp(2rem, 5vw, 4rem);
            margin-bottom: 20px;
            background: linear-gradient(135deg, var(--primary-green), var(--light-green));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            font-weight: 700;
            letter-spacing: -1px;
          }

          .hero-content p {
            font-size: clamp(0.9rem, 2vw, 1.1rem);
            line-height: 1.7;
            font-weight: 300;
          }

          .story-section, .milestones-section, .contact-section {
            padding: 50px 20px;
            max-width: 1200px;
            margin: 0 auto;
          }

          .story-section {
            text-align: center;
          }

          .story-section h2, .contact-content h2 {
            font-size: clamp(1.8rem, 4vw, 2.5rem);
            color: var(--primary-green);
            margin-bottom: 25px;
          }

          .story-section p {
            font-size: clamp(0.9rem, 2vw, 1.1rem);
            line-height: 1.7;
            max-width: 800px;
            margin: 0 auto;
          }

          .timeline {
            position: relative;
            max-width: 1000px;
            margin: 0 auto;
          }

          .timeline::after {
            content: '';
            position: absolute;
            width: 4px;
            background: var(--primary-green);
            top: 0;
            bottom: 0;
            left: 50%;
            margin-left: -2px;
            border-radius: 2px;
          }

          .milestone-card {
            padding: 25px;
            position: relative;
            width: calc(50% - 30px);
            background: rgba(46, 204, 113, 0.1);
            border: 1px solid var(--primary-green);
            border-radius: 12px;
            margin: 30px 0;
            transition: all 0.4s ease;
          }

          .milestone-card::after {
            content: '';
            position: absolute;
            width: 20px;
            height: 20px;
            background: var(--primary-green);
            border: 4px solid #fff;
            border-radius: 50%;
            top: 50%;
            transform: translateY(-50%);
            z-index: 1;
          }

          .milestone-card:nth-child(odd) {
            left: 0;
          }

          .milestone-card:nth-child(even) {
            left: 50%;
            margin-left: 30px;
          }

          .milestone-card:nth-child(odd)::after {
            right: -42px;
          }

          .milestone-card:nth-child(even)::after {
            left: -42px;
          }

          .milestone-card:nth-child(odd)::before {
            content: '';
            position: absolute;
            width: 30px;
            height: 2px;
            background: var(--primary-green);
            top: 50%;
            right: -30px;
          }

          .milestone-card:nth-child(even)::before {
            content: '';
            position: absolute;
            width: 30px;
            height: 2px;
            background: var(--primary-green);
            top: 50%;
            left: -30px;
          }

          .milestone-year {
            font-size: clamp(1.2rem, 3vw, 1.8rem);
            color: var(--primary-green);
            margin-bottom: 10px;
          }

          .dark-theme .milestone-card:hover {
            box-shadow: 10px 10px 30px rgba(46, 204, 113, 0.4);
          }

          .light-theme .milestone-card:hover {
            box-shadow: 10px 10px 30px rgba(94, 171, 112, 0.3);
          }

          .contact-section {
            text-align: center;
          }

          .contact-content {
            max-width: 600px;
            margin: 0 auto;
          }

          .contact-form {
            display: grid;
            gap: 15px;
          }

          .form-input {
            padding: 12px;
            border: 1px solid var(--primary-green);
            background: rgba(46, 204, 113, 0.1);
            border-radius: 8px;
            color: inherit;
            font-family: inherit;
          }

          .submit-btn {
            padding: 12px 25px;
            background: var(--primary-green);
            border: none;
            border-radius: 8px;
            color: #fff;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .submit-btn:hover {
            background: var(--dark-green);
            transform: translateY(-3px);
          }

          .submit-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
          
          .form-message {
            margin-top: 15px;
            padding: 10px;
            border-radius: 5px;
            text-align: center;
            transition: opacity 0.3s ease;
          }

          .form-message.success {
            background-color: rgba(46, 204, 113, 0.2);
            color: #2ecc71;
          }

          .form-message.error {
            background-color: rgba(231, 76, 60, 0.2);
            color: #e74c3c;
          }

          /* Mobile responsive styles */
          @media screen and (max-width: 768px) {
            .timeline::after {
              left: 31px;
            }
            
            .milestone-card {
              width: calc(100% - 80px);
              margin-left: 80px !important;
              left: 0 !important;
            }
            
            .milestone-card::after {
              left: -42px !important;
            }
            
            .milestone-card::before {
              left: -30px !important;
            }

            .custom-cursor, .cursor-dot {
              display: none;
            }
            
            .about-hero {
              height: auto;
              min-height: 40vh;
              padding: 40px 20px;
            }
          }

          @media (max-width: 480px) {
            .milestone-card {
              padding: 15px;
              margin-left: 70px !important;
              width: calc(100% - 70px);
            }
          }
        `}
      </style>

      <div className={themeClass}>
        {windowWidth > 768 && (
          <>
            <div ref={cursorRef} className="custom-cursor" />
            <div ref={cursorDotRef} className="cursor-dot" />
          </>
        )}

        {/* Desktop Navigation */}
        <nav style={styles.desktopNav}>
          <Link to="/" style={styles.logoSection}>
            <FontAwesomeIcon icon={faLeaf} style={styles.logoIcon} />
            <span>The Sustainable Formula</span>
          </Link>
          
          <div style={styles.navControls}>
            <div style={styles.desktopNavLinks}>
              <Link to="/team" style={styles.navLink}>Our Team</Link>
              <Link to="/about" style={styles.navLink}>About Us</Link>
              <Link to="/initiatives" style={styles.navLink}>Initiatives</Link>
              <Link to="/donate" style={styles.navLink}>Donate</Link>
            </div>
            
            <button 
            style={styles.themeToggle}
            onClick={toggleTheme}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <CustomSunIcon />
            ) : (
              <FontAwesomeIcon icon={faMoon} />
            )}
          </button>
            
            <button 
              style={styles.hamburger} 
              onClick={toggleMobileMenu}
              aria-label="Menu"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <div style={styles.mobileMenu}>
          <button 
            style={styles.mobileMenuClose} 
            onClick={toggleMobileMenu}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div style={styles.mobileNavLinks}>
            <Link 
              to="/team" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Our Team
            </Link>
            <Link 
              to="/about" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              About Us
            </Link>
            <Link 
              to="/initiatives" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Initiatives
            </Link>
            <Link 
              to="/donate" 
              style={styles.mobileNavLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              Donate
            </Link>
          </div>
        </div>

        <section className="about-hero">
          <div className="hero-content">
            <h1>About The Sustainable Formula</h1>
            <p>We are a team of high school students on a mission to make Bengaluru a greener, more sustainable city. Through projects like tree planting, green roofs, and rainwater harvesting, we aim to drive real change. By engaging our community and raising awareness on social media, we inspire others to join us in creating a lasting impact for a sustainable future.</p>
          </div>
        </section>

        <section className="story-section">
          <h2>Our Story</h2>
          <p>Founded by a group of passionate students, our nonprofit began with a simple yet powerful idea: to inspire communities to take meaningful action for a sustainable future. What started as small tree-planting drives has grown into a dynamic movement focused on empowering individuals, schools, and neighborhoods to embrace eco-friendly practices. United by our love for the planet and a determination to make a difference, we strive to create innovative solutions to pressing environmental challenges, proving that even small actions, when done together, can lead to extraordinary change.</p>
        </section>

        <section className="milestones-section">
          <div className="timeline">
            <div className="milestone-card">
              <div className="milestone-year">June 2024</div>
              <p>"The Sustainable Formula" was founded with over 10 people being recruited as our core team, united to tackle environmental issues in Bengaluru.</p>
            </div>
            <div className="milestone-card">
              <div className="milestone-year">July 2024</div>
              <p>We established our presence on social media platforms like Instagram and LinkedIn to spread awareness on our work. </p>
            </div>
            <div className="milestone-card">
              <div className="milestone-year">August 2024</div>
              <p>Embarked on our "Forest Restoration Project" aiding in the planation of over 1000 trees.</p>
            </div>
            <div className="milestone-card">
              <div className="milestone-year">Present</div>
              <p>Planning and working towards our future endeavors to make Bengaluru sustainable through initiatives like Green Roofs and Rain Water Harvesting Systems for those in need.</p>
            </div>
          </div>
        </section>

        <section className="contact-section">
          <div className="contact-content">
            <h2>Get in Touch</h2>
            <form className="contact-form" onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="form-input"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <textarea
                name="message"
                placeholder="Message"
                className="form-input"
                rows="4"
                value={formData.message}
                onChange={handleInputChange}
                required
              ></textarea>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={status.submitting}
              >
                {status.submitting ? 'Sending...' : 'Send Message'}
              </button>
              
              {status.info.msg && (
                <div className={`form-message ${status.info.error ? 'error' : 'success'}`}>
                  {status.info.msg}
                </div>
              )}
            </form>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutUs;